import Client from 'shopify-buy';

// Initialize the Shopify client with proper error handling
// Remove any quotes or comments from the environment variables
const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';

// console.log('Shopify Config:', { 
//   domain, 
//   hasToken: !!storefrontAccessToken,
//   apiVersion 
// });

// Create a dummy client for testing if we can't connect to Shopify
let shopifyClient;
try {
  shopifyClient = Client.buildClient({
    domain,
    storefrontAccessToken,
    apiVersion
  });
  console.log('Shopify client initialized successfully');
} catch (error) {
  console.error('Error initializing Shopify client:', error);
  // Create a dummy client that returns empty arrays for testing
  shopifyClient = {
    product: {
      fetchAll: async () => {
        console.log('Using dummy product data');
        return [
          {
            id: 'gid://shopify/Product/1',
            title: 'Dummy Product 1',
            handle: 'dummy-product-1',
            description: 'This is a dummy product for testing',
            images: [{ src: 'https://via.placeholder.com/150' }],
            variants: [
              {
                id: 'gid://shopify/ProductVariant/1',
                title: 'Coles',
                price: '4.50',
                sku: 'DUMMY-1-COLES',
                available: true,
                selectedOptions: [{ name: 'Supplier', value: 'Coles' }]
              },
              {
                id: 'gid://shopify/ProductVariant/2',
                title: 'Woolworths',
                price: '4.50',
                sku: 'DUMMY-1-WOOLWORTHS',
                available: true,
                selectedOptions: [{ name: 'Supplier', value: 'Woolworths' }]
              }
            ],
            // Add dummy metafields for testing
            metafields: [
              { key: 'on_special', value: 'true', namespace: 'grocerize' },
              { key: 'Is half price or better', value: 'false', namespace: 'grocerize' }
            ]
          }
        ];
      }
    }
  };
}

// Function to fetch all products
// Helper function to fetch products from a specific collection
async function fetchCollectionProducts(collectionHandle: string, limit: number, domain: string, token: string, apiVersion: string) {
  // Create a GraphQL query to fetch products from the specified collection
  const collectionQuery = `
    {
      collectionByHandle(handle: "${collectionHandle}") {
        id
        title
        products(first: ${limit}, sortKey: MANUAL) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              title
              handle
              description
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    sku
                    availableForSale
                  }
                }
              }
              images(first: 1) {
                edges {
                  node {
                    originalSrc
                    altText
                  }
                }
              }
              metafields(identifiers: [
                {namespace: "global", key: "on_special"},
                {namespace: "global", key: "is_half_price_or_better"},
                {namespace: "pinned", key: "on_special"},
                {namespace: "pinned", key: "Is half price or better"},
                {namespace: "data", key: "json"},
                {namespace: "pinned", key: "Data"},
                {namespace: "pinned", key: "data"},
                {namespace: "global", key: "Data"},
                {namespace: "global", key: "data"}
              ]) {
                namespace
                key
                value
              }
            }
          }
        }
      }
    }
  `;

  // Execute the GraphQL query
  const url = `https://${domain}/api/${apiVersion}/graphql.json`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    },
    body: JSON.stringify({ query: collectionQuery })
  });

  if (!response.ok) {
    console.error(`Collection query for ${collectionHandle} failed with status ${response.status}`);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }

  const result = await response.json();

  if (result.errors) {
    console.error(`GraphQL errors when fetching collection ${collectionHandle}:`, result.errors);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }

  // Extract products from the collection
  if (result.data?.collectionByHandle?.products?.edges) {
    const edges = result.data.collectionByHandle.products.edges;
    const products = edges.map(edge => {
      const product = edge.node;
      
      // Format the product to match the structure expected by the transformer
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        description: product.description,
        images: product.images.edges.map(img => ({
          src: img.node.originalSrc,
          altText: img.node.altText
        })),
        variants: product.variants.edges.map(variant => ({
          id: variant.node.id,
          title: variant.node.title,
          price: variant.node.price.amount,
          sku: variant.node.sku,
          available: variant.node.availableForSale
        })),
        metafields: product.metafields || []
      };
    });
    
    // Extract pagination info
    const pageInfo = result.data.collectionByHandle.products.pageInfo;
    
    console.log(`Fetched ${products.length} products from collection ${collectionHandle}`);
    
    return { 
      products,
      pageInfo: {
        hasNextPage: pageInfo.hasNextPage,
        endCursor: pageInfo.endCursor
      }
    };
  } else {
    console.log(`No products found in collection ${collectionHandle}`);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

// Helper function to fetch products from the general catalog
async function fetchGeneralProducts(limit: number, domain: string, token: string, apiVersion: string) {
  // Create a GraphQL query to fetch products with pagination
  const generalQuery = `
    {
      products(first: ${limit}) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          cursor
          node {
            id
            title
            handle
            description
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  sku
                  availableForSale
                }
              }
            }
            images(first: 1) {
              edges {
                node {
                  originalSrc
                  altText
                }
              }
            }
            metafields(identifiers: [
              {namespace: "global", key: "on_special"},
              {namespace: "global", key: "is_half_price_or_better"},
              {namespace: "pinned", key: "on_special"},
              {namespace: "pinned", key: "Is half price or better"},
              {namespace: "data", key: "json"},
              {namespace: "pinned", key: "Data"},
              {namespace: "pinned", key: "data"},
              {namespace: "global", key: "Data"},
              {namespace: "global", key: "data"}
            ]) {
              namespace
              key
              value
            }
          }
        }
      }
    }
  `;

  // Execute the GraphQL query
  const url = `https://${domain}/api/${apiVersion}/graphql.json`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    },
    body: JSON.stringify({ query: generalQuery })
  });

  if (!response.ok) {
    console.error(`General products query failed with status ${response.status}`);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }

  const result = await response.json();

  if (result.errors) {
    console.error('GraphQL errors when fetching general products:', result.errors);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }

  // Extract products from the response
  if (result.data?.products?.edges) {
    const products = result.data.products.edges.map(edge => {
      const product = edge.node;
      
      // Format the product to match the structure expected by the transformer
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        description: product.description,
        images: product.images.edges.map(img => ({
          src: img.node.originalSrc,
          altText: img.node.altText
        })),
        variants: product.variants.edges.map(variant => ({
          id: variant.node.id,
          title: variant.node.title,
          price: variant.node.price.amount,
          sku: variant.node.sku,
          available: variant.node.availableForSale
        })),
        metafields: product.metafields || []
      };
    });
    
    // Extract pagination info
    const pageInfo = result.data.products.pageInfo;
    
    console.log(`Fetched ${products.length} general products`);
    
    return { 
      products,
      pageInfo: {
        hasNextPage: pageInfo.hasNextPage,
        endCursor: pageInfo.endCursor
      }
    };
  } else {
    console.log('No general products found');
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

// Import the activeSearchRequest flag from itemActions
// We'll use a function to access the flag instead of directly importing it to avoid circular dependencies
export let isActiveSearchInProgress = () => false;

// Function to register the active search checker (called from itemActions.ts)
export function registerActiveSearchChecker(checker: () => boolean) {
  isActiveSearchInProgress = checker;
}

// Special function to fetch homepage products with Half Price Homepage collection prioritized
export async function fetchHomepageProducts(limit: number = 100) {
  try {
    // Check if there's an active search in progress - if so, don't fetch homepage products
    if (isActiveSearchInProgress()) {
      console.log('Active search in progress - skipping homepage products fetch to prevent override');
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    console.log(`Fetching homepage products prioritizing Half Price Homepage collection, limit: ${limit}`);
    
    // We need to fetch products using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/["']/g, '').trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/["']/g, '').trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/["']/g, '').trim() || '2023-10';
    
    // First fetch products from the Half Price Homepage collection
    const halfPriceResult = await fetchCollectionProducts('new-half-price', limit, domain, storefrontAccessToken, apiVersion);
    
    // If we have enough products from the collection, return them
    if (halfPriceResult.products.length >= limit) {
      console.log(`Using just Half Price products (${halfPriceResult.products.length} items)`);
      return {
        products: halfPriceResult.products.slice(0, limit),
        pageInfo: halfPriceResult.pageInfo
      };
    }
    
    // If we need more products, fetch additional ones from the general catalog
    if (halfPriceResult.products.length < limit) {
      const remainingLimit = limit - halfPriceResult.products.length;
      console.log(`Need ${remainingLimit} more products from general catalog`);
      
      // Fetch remaining products from general catalog
      const generalResult = await fetchGeneralProducts(remainingLimit, domain, storefrontAccessToken, apiVersion);
      
      // Combine both sets of products
      const combinedProducts = [...halfPriceResult.products, ...generalResult.products];
      console.log(`Returning combined ${combinedProducts.length} products`);
      
      return {
        products: combinedProducts.slice(0, limit),
        pageInfo: generalResult.pageInfo
      };
    }
    
    // Fallback - should not reach here if above logic is correct
    return halfPriceResult;
  } catch (error) {
    console.error('Error fetching homepage products:', error);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

export async function fetchAllProducts(cursor?: string, limit: number = 100) {
  try {
    console.log(`Fetching all products, cursor: ${cursor || 'initial'}, limit: ${limit}`);
    
    // We need to fetch products using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/["']/g, '').trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/["']/g, '').trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/["']/g, '').trim() || '2023-10';
    
    // Check if this is the initial fetch (no cursor provided)
    // If so, prioritize products from the "Half Price Homepage" collection
    if (!cursor) {
      console.log('First page load - prioritizing Half Price Homepage collection');
      
      // Fetch products from the Half Price Homepage collection
      const halfPriceResult = await fetchCollectionProducts('new-half-price', limit, domain, storefrontAccessToken, apiVersion);
      
      // If we have enough products from the collection, return them
      if (halfPriceResult.products.length >= limit) {
        console.log(`Using just Half Price products (${halfPriceResult.products.length} items)`);
        return {
          products: halfPriceResult.products.slice(0, limit),
          pageInfo: halfPriceResult.pageInfo
        };
      }
      
      // If we need more products, fetch additional ones from the general catalog
      if (halfPriceResult.products.length > 0 && halfPriceResult.products.length < limit) {
        const remainingLimit = limit - halfPriceResult.products.length;
        console.log(`Need ${remainingLimit} more products from general catalog`);
        
        // Fetch remaining products from general catalog
        const generalResult = await fetchGeneralProducts(remainingLimit, domain, storefrontAccessToken, apiVersion);
        
        // Combine both sets of products
        const combinedProducts = [...halfPriceResult.products, ...generalResult.products];
        console.log(`Returning combined ${combinedProducts.length} products`);
        
        return {
          products: combinedProducts.slice(0, limit),
          pageInfo: generalResult.pageInfo
        };
      }
    }
    
    // For paginated requests or if Half Price collection is empty, use standard products query
    // Create a GraphQL query to fetch products with pagination
    const afterCursor = cursor ? `, after: "${cursor}"` : '';
    
    const query = `
      {
        products(first: ${limit}${afterCursor}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              title
              handle
              description
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    sku
                    availableForSale
                  }
                }
              }
              images(first: 1) {
                edges {
                  node {
                    originalSrc
                    altText
                  }
                }
              }
              metafields(identifiers: [
                {namespace: "global", key: "on_special"},
                {namespace: "global", key: "is_half_price_or_better"},
                {namespace: "pinned", key: "on_special"},
                {namespace: "pinned", key: "Is half price or better"},
                {namespace: "data", key: "json"},
                {namespace: "pinned", key: "Data"},
                {namespace: "pinned", key: "data"},
                {namespace: "global", key: "Data"},
                {namespace: "global", key: "data"}
              ]) {
                namespace
                key
                value
              }
            }
          }
        }
      }
    `;
    
    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });
    
    if (!response.ok) {
      console.error(`Shopify API responded with status ${response.status} when fetching all products`);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors when fetching all products:', result.errors);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    // Extract products and pagination info from the response
    if (result.data && result.data.products) {
      const products = result.data.products.edges.map(edge => {
        const product = edge.node;
        
        // Format the product to match the structure expected by the transformer
        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          description: product.description,
          images: product.images.edges.map(img => ({
            src: img.node.originalSrc,
            altText: img.node.altText
          })),
          variants: product.variants.edges.map(variant => ({
            id: variant.node.id,
            title: variant.node.title,
            price: variant.node.price.amount,
            sku: variant.node.sku,
            available: variant.node.availableForSale
          })),
          metafields: product.metafields || []
        };
      });
      
      // Extract pagination info
      const pageInfo = result.data.products.pageInfo;
      
      console.log(`Fetched ${products.length} products`);
      console.log(`Pagination info: hasNextPage: ${pageInfo.hasNextPage}, endCursor: ${pageInfo.endCursor}`);
      
      return { 
        products,
        pageInfo: {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor
        }
      };
    } else {
      console.log('No products found');
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
  } catch (error) {
    console.error('Error fetching all products:', error);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
};

// Function to search for products
export async function searchProducts(searchTerm: string, cursor?: string, limit: number = 100, enhancedRanking: boolean = false) {
  try {
    console.log(`Searching for products with term: ${searchTerm}, cursor: ${cursor || 'initial'}, limit: ${limit}`);
    
    // We need to search for products using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query to search for products
    // Add 'title:' prefix to search term to prioritize title matches
    // This makes the search more relevant by focusing on products with the search term in the title
    const enhancedSearchTerm = `title:${searchTerm}`;
    
    let query = `
      {
        products(first: ${limit}, query: "${enhancedSearchTerm}", sortKey: RELEVANCE${cursor ? `, after: "${cursor}"` : ''}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              title
              handle
              description
              productType
              vendor
              tags
              collections(first: 5) {
                edges {
                  node {
                    id
                    title
                    handle
                  }
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    sku
                    availableForSale
                  }
                }
              }
              images(first: 1) {
                edges {
                  node {
                    originalSrc
                    altText
                  }
                }
              }
              metafields(identifiers: [
                {namespace: "global", key: "on_special"},
                {namespace: "global", key: "is_half_price_or_better"},
                {namespace: "pinned", key: "on_special"},
                {namespace: "pinned", key: "Is half price or better"},
                {namespace: "data", key: "json"},
                {namespace: "pinned", key: "Data"},
                {namespace: "pinned", key: "data"},
                {namespace: "global", key: "Data"},
                {namespace: "global", key: "data"}
              ]) {
                namespace
                key
                value
              }
            }
          }
        }
      }
    `;
    
    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });
    
    if (!response.ok) {
      console.error(`Shopify API responded with status ${response.status} when searching for products`);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors when searching for products:', result.errors);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    // Extract products and pagination info from the response
    if (result.data && result.data.products) {
      // Get the products from the response
      let products = result.data.products.edges.map(edge => {
        const product = edge.node;
        
        // Format the product to match the structure expected by the transformer
        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          description: product.description,
          productType: product.productType,
          vendor: product.vendor,
          tags: product.tags || [],
          collections: product.collections?.edges.map(col => ({
            id: col.node.id,
            title: col.node.title,
            handle: col.node.handle
          })) || [],
          images: product.images.edges.map(img => ({
            src: img.node.originalSrc,
            altText: img.node.altText
          })),
          variants: product.variants.edges.map(variant => ({
            id: variant.node.id,
            title: variant.node.title,
            price: variant.node.price.amount,
            sku: variant.node.sku,
            available: variant.node.availableForSale
          })),
          metafields: product.metafields || [],
          relevanceScore: 0 // Will be calculated below
        };
      });
      
      // Only apply enhanced ranking if explicitly requested (for "View all results" search)
      if (enhancedRanking) {
        // Enhanced relevance scoring for better search results
        const searchTermLower = searchTerm.toLowerCase();
        const searchWords = searchTermLower.split(/\s+/).filter(word => word.length > 1);
        
        products = products.map(product => {
          let score = 0;
          const titleLower = product.title.toLowerCase();
          
          // ----- Title matching (highest priority) -----
          
          // Perfect match: exact match for full title
          if (titleLower === searchTermLower) {
            score += 1000; // Much higher priority for exact matches
          }
          // Strong match: title starts with search term (e.g., "Eggs Free Range")
          else if (titleLower.startsWith(searchTermLower)) {
            score += 500;
          }
          // Good match: search term is a whole word in the title
          else if (new RegExp(`\\b${searchTermLower}\\b`).test(titleLower)) {
            score += 300;
          }
          // Decent match: search term appears anywhere in the title
          else if (titleLower.includes(searchTermLower)) {
            score += 200;
          }
          
          // Multi-word search term partial matching
          if (searchWords.length > 1) {
            // Count how many words from search query appear in the title
            const matchingWordCount = searchWords.filter(word => titleLower.includes(word)).length;
            const matchRatio = matchingWordCount / searchWords.length;
            score += Math.round(100 * matchRatio);
          }
          
          // Prioritize products that have the focus word in the beginning of the title
          // For example, if searching for "eggs", "Eggs Free Range" should rank higher than "Free Range Eggs"
          const mainWord = searchWords[0] || searchTermLower;
          const wordPositionInTitle = titleLower.indexOf(mainWord);
          if (wordPositionInTitle >= 0) {
            // Give higher scores to words appearing earlier in the title
            // A position of 0 would get the full 150 points, decreasing as position increases
            score += Math.max(0, 150 - wordPositionInTitle * 5);
          }
          
          // ----- Product type matching -----
          
          // Boost products that are exactly the type being searched for
          // e.g., If searching for "eggs", boost products with type "Eggs"
          if (product.productType && product.productType.toLowerCase() === searchTermLower) {
            score += 200;
          }
          // Partial match on product type
          else if (product.productType && product.productType.toLowerCase().includes(searchTermLower)) {
            score += 100;
          }
          
          // ----- Tag and collection matching (secondary signals) -----
          
          // Check for exact tag matches (e.g., "eggs" tag)
          if (product.tags && Array.isArray(product.tags)) {
            // Exact tag match
            const exactTagMatches = product.tags.filter(tag => 
              tag.toLowerCase() === searchTermLower || 
              tag.toLowerCase().split('-').includes(searchTermLower)
            );
            score += exactTagMatches.length * 80;
            
            // Partial tag match
            const partialTagMatches = product.tags.filter(tag => 
              tag.toLowerCase().includes(searchTermLower) && !exactTagMatches.includes(tag)
            );
            score += partialTagMatches.length * 30;
          }
          
          // Collection matching
          if (product.collections && Array.isArray(product.collections)) {
            // Exact collection title match
            const exactCollectionMatches = product.collections.filter(collection => 
              collection.title.toLowerCase() === searchTermLower ||
              collection.title.toLowerCase().split(' ').includes(searchTermLower)
            );
            score += exactCollectionMatches.length * 50;
            
            // Partial collection match
            const partialCollectionMatches = product.collections.filter(collection => 
              collection.title.toLowerCase().includes(searchTermLower) && 
              !exactCollectionMatches.some(exact => exact.id === collection.id)
            );
            score += partialCollectionMatches.length * 20;
          }
          
          // Set the final relevance score
          return {
            ...product,
            relevanceScore: score
          };
        });
        
        // Sort products by relevance score (highest first)
        // For products with the same score, sort alphabetically by title
        products.sort((a, b) => {
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore; // Sort by score descending
          }
          // For same scores, sort alphabetically
          return a.title.localeCompare(b.title);
        });
        
        // Debug scores for the top 5 results
        console.log('Top 5 search results with enhanced ranking:');
        products.slice(0, 5).forEach((product, index) => {
          console.log(`${index + 1}. ${product.title} (Score: ${product.relevanceScore})`);
        });
      } else {
        // Default behavior: simple relevance scoring from Shopify's sorting
        // Just add a basic relevance score for consistent interface
        products = products.map(product => ({
          ...product,
          relevanceScore: 0
        }));
      }
      
      // Extract pagination info
      const pageInfo = result.data.products.pageInfo;
      
      console.log(`Found ${products.length} products matching search term: ${searchTerm}`);
      console.log(`Pagination info: hasNextPage: ${pageInfo.hasNextPage}, endCursor: ${pageInfo.endCursor}`);
      
      return { 
        products,
        pageInfo: {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor
        }
      };
    } else {
      console.log(`No products found matching search term: ${searchTerm}`);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
  } catch (error) {
    console.error(`Error searching for products with term: ${searchTerm}:`, error);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
};

// Function to fetch a single product by ID
export async function fetchProductById(productId: string) {
  try {
    console.log(`Fetching product with ID: ${productId}`);
    const product = await shopifyClient.product.fetch(productId);
    console.log('Fetched product:', product.title);
    return product;
  } catch (error) {
    console.error(`Error fetching product ${productId} from Shopify:`, error);
    throw error;
  }
};

// Function to fetch products by collection
export async function fetchProductsByCollection(collectionId: string) {
  try {
    console.log(`Fetching collection with ID: ${collectionId}`);
    const collection = await shopifyClient.collection.fetchWithProducts(collectionId);
    console.log('Fetched collection products count:', collection.products.length);
    return collection.products;
  } catch (error) {
    console.error(`Error fetching collection ${collectionId} from Shopify:`, error);
    throw error;
  }
};

// Function to fetch products by collection handle
export async function fetchProductsByCollectionHandle(collectionHandle: string, cursor?: string, limit: number = 100) {
  try {
    // DEBUGGING: Add detailed logs for cursor tracking
    console.log(`------ PAGINATION DEBUG ------`);
    console.log(`Collection: ${collectionHandle}`);
    console.log(`Input cursor: ${cursor || 'INITIAL'}`);
    console.log(`Cursor length: ${cursor ? cursor.length : 0}`);
    if (cursor) {
      try {
        // Try to decode the cursor to understand its structure
        const decodedCursor = Buffer.from(cursor, 'base64').toString('utf-8');
        console.log(`Decoded cursor: ${decodedCursor}`);
      } catch (e) {
        console.log(`Failed to decode cursor: ${e.message}`);
      }
    }
    console.log(`---------------------------`);
    
    console.log(`Fetching products for collection handle: ${collectionHandle}, cursor: ${cursor || 'initial'}, limit: ${limit}`);
    
    // We need to fetch the collection and its products using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query to fetch the collection and its products with pagination
    // If cursor is provided, use it for pagination
    // DEBUGGING: Verify cursor formatting in the GraphQL query
    const afterCursor = cursor ? `, after: "${cursor}"` : '';
    console.log(`GraphQL cursor parameter: ${afterCursor}`);
    
    const query = `
      {
        collectionByHandle(handle: "${collectionHandle}") {
          id
          title
          handle
          products(first: ${limit}${afterCursor}, sortKey: COLLECTION_DEFAULT) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                title
                handle
                description
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      sku
                      availableForSale
                    }
                  }
                }
                images(first: 1) {
                  edges {
                    node {
                      originalSrc
                      altText
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "global", key: "on_special"},
                  {namespace: "global", key: "is_half_price_or_better"},
                  {namespace: "pinned", key: "on_special"},
                  {namespace: "pinned", key: "Is half price or better"},
                  {namespace: "data", key: "json"},
                  {namespace: "pinned", key: "Data"},
                  {namespace: "pinned", key: "data"},
                  {namespace: "global", key: "Data"},
                  {namespace: "global", key: "data"}
                ]) {
                  namespace
                  key
                  value
                }
              }
            }
          }
        }
      }
    `;
    
    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });
    
    if (!response.ok) {
      console.error(`Shopify API responded with status ${response.status} when fetching collection products`);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors when fetching collection products:', result.errors);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
    
    // Extract products and pagination info from the response
    if (result.data && result.data.collectionByHandle && result.data.collectionByHandle.products) {
      const products = result.data.collectionByHandle.products.edges.map(edge => {
        const product = edge.node;
        
        // Format the product to match the structure expected by the transformer
        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          description: product.description,
          images: product.images.edges.map(img => ({
            src: img.node.originalSrc,
            altText: img.node.altText
          })),
          variants: product.variants.edges.map(variant => ({
            id: variant.node.id,
            title: variant.node.title,
            price: variant.node.price.amount,
            sku: variant.node.sku,
            available: variant.node.availableForSale
          })),
          metafields: product.metafields || []
        };
      });
      
      // Extract pagination info
      const pageInfo = result.data.collectionByHandle.products.pageInfo;
      
      // DEBUGGING: Add detailed logs for cursor result
      console.log(`Fetched ${products.length} products for collection: ${collectionHandle}`);
      console.log(`Pagination info: hasNextPage: ${pageInfo.hasNextPage}, endCursor: ${pageInfo.endCursor}`);
      
      console.log(`------ PAGINATION DEBUG RESULT ------`);
      console.log(`Collection: ${collectionHandle}`);
      console.log(`Output cursor: ${pageInfo.endCursor || 'NONE'}`);
      console.log(`Output cursor length: ${pageInfo.endCursor ? pageInfo.endCursor.length : 0}`);
      if (pageInfo.endCursor) {
        try {
          // Try to decode the result cursor
          const decodedCursor = Buffer.from(pageInfo.endCursor, 'base64').toString('utf-8');
          console.log(`Decoded result cursor: ${decodedCursor}`);
        } catch (e) {
          console.log(`Failed to decode result cursor: ${e.message}`);
        }
      }
      
      // Check for cursor reuse issues
      if (cursor && pageInfo.endCursor && cursor === pageInfo.endCursor) {
        console.error(`⚠️ CURSOR REUSE DETECTED: Collection ${collectionHandle} returned the same cursor!`);
        // Force a different cursor by adding a small offset if available
        if (pageInfo.hasNextPage) {
          console.log(`Attempting to fix by modifying the cursor...`);
          // Use a slightly different approach - if we have products, use the last product's ID
          if (products.length > 0) {
            // Using the last product's timestamp to create a unique cursor (not using the variable directly)
            const modifiedCursor = `${pageInfo.endCursor}_modified_${Date.now()}`;
            console.log(`Generated modified cursor: ${modifiedCursor}`);
            return { 
              products,
              pageInfo: {
                hasNextPage: pageInfo.hasNextPage,
                endCursor: modifiedCursor
              }
            };
          }
        } else {
          console.log(`No more pages, pagination should stop naturally.`);
        }
      }
      console.log(`---------------------------`);
      
      return { 
        products,
        pageInfo: {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor
        }
      };
    } else {
      console.log(`No collection found with handle: ${collectionHandle}`);
      return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }
  } catch (error) {
    console.error(`Error fetching products for collection ${collectionHandle}:`, error);
    return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
};

// Function to fetch all collections
export async function fetchAllCollections() {
  try {
    console.log('Fetching all collections from Shopify...');
    
    // We need to fetch collections using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query to fetch all collections
    const query = `
      {
        collections(first: 250) {
          edges {
            node {
              id
              title
              handle
              description
              image {
                originalSrc
                altText
              }
              products(first: 1) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `;
    
    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });
    
    if (!response.ok) {
      console.error(`Shopify API responded with status ${response.status} when fetching collections`);
      return [];
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors when fetching collections:', result.errors);
      return [];
    }
    
    // Extract collections from the response
    if (result.data && result.data.collections && result.data.collections.edges) {
      const collections = result.data.collections.edges.map(edge => {
        const collection = edge.node;
        // Check if collection has products
        const hasProducts = collection.products.edges.length > 0;
        return {
          id: collection.id,
          title: collection.title,
          handle: collection.handle,
          description: collection.description,
          image: collection.image,
          hasProducts
        };
      });
      
      console.log(`Fetched ${collections.length} collections from Shopify`);
      return collections;
    } else {
      console.log('No collections found in Shopify store');
      return [];
    }
  } catch (error) {
    console.error('Error fetching collections from Shopify:', error);
    return [];
  }
};

// Function to fetch the main menu from Shopify
export async function fetchShopifyMenu(menuHandle: string = 'main-menu') {
  try {
    console.log(`Fetching menu with handle: ${menuHandle} from Shopify...`);
    
    // We need to fetch the menu using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query to fetch the menu
    const query = `
      {
        menu(handle: "${menuHandle}") {
          id
          handle
          title
          items {
            id
            title
            url
            items {
              id
              title
              url
              items {
                id
                title
                url
              }
            }
          }
        }
      }
    `;
    
    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });
    
    if (!response.ok) {
      console.error(`Shopify API responded with status ${response.status} when fetching menu`);
      return null;
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors when fetching menu:', result.errors);
      return null;
    }
    
    // Extract menu from the response
    if (result.data && result.data.menu) {
      console.log(`Fetched menu: ${result.data.menu.title}`);
      return result.data.menu;
    } else {
      console.log(`No menu found with handle: ${menuHandle}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching menu from Shopify:`, error);
    return null;
  }
};

// Cache keys and expiration time
const COLLECTION_COUNTS_CACHE_KEY = 'shopify_collection_product_counts';
const COLLECTION_COUNTS_CACHE_TIMESTAMP = 'shopify_collection_product_counts_timestamp';
const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Function to get cached collection product counts
function getCachedCollectionCounts() {
  try {
    const cachedTimestampStr = localStorage.getItem(COLLECTION_COUNTS_CACHE_TIMESTAMP);
    if (!cachedTimestampStr) return null;
    
    const cachedTimestamp = parseInt(cachedTimestampStr, 10);
    const now = Date.now();
    
    // Check if cache has expired
    if (now - cachedTimestamp > CACHE_EXPIRATION_MS) {
      console.log('Collection counts cache has expired');
      return null;
    }
    
    const cachedCountsStr = localStorage.getItem(COLLECTION_COUNTS_CACHE_KEY);
    if (!cachedCountsStr) return null;
    
    const cachedCounts = JSON.parse(cachedCountsStr);
    console.log('Using cached collection product counts', cachedCounts);
    return cachedCounts;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
}

// Function to save collection product counts to cache
function saveCollectionCountsToCache(counts: Record<string, number>) {
  try {
    localStorage.setItem(COLLECTION_COUNTS_CACHE_KEY, JSON.stringify(counts));
    localStorage.setItem(COLLECTION_COUNTS_CACHE_TIMESTAMP, Date.now().toString());
    console.log('Saved collection product counts to cache');
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
}

// Function to fetch collection product counts
export async function fetchCollectionProductCounts() {
  try {
    // First check if we have a valid cache
    const cachedCounts = getCachedCollectionCounts();
    if (cachedCounts) {
      return cachedCounts;
    }
    
    console.log('Fetching collection product counts from Shopify...');
    
    // We need to fetch collections with product counts using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query to fetch all collections with their products
    // Since we can't directly get the count, we'll fetch the products and count them
    const query = `
      {
        collections(first: 250) {
          edges {
            node {
              id
              handle
              title
              products(first: 250) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `;
    
    console.log('GraphQL query for collection counts:', query);
    
    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });
    
    if (!response.ok) {
      console.error(`Shopify API responded with status ${response.status} when fetching collection counts`);
      console.error('Response:', await response.text());
      
      // Fallback to fetching collections one by one with product counts
      return await fetchCollectionProductCountsAlternative();
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors when fetching collection counts:', result.errors);
      // Fallback to fetching collections one by one with product counts
      return await fetchCollectionProductCountsAlternative();
    }
    
    // Create a map of collection handles to product counts
    const productCountMap: Record<string, number> = {};
    
    if (result.data && result.data.collections && result.data.collections.edges) {
      result.data.collections.edges.forEach((edge: any) => {
        const collection = edge.node;
        // Count the products in the collection
        const productCount = collection.products?.edges?.length || 0;
        productCountMap[collection.handle] = productCount;
        console.log(`Collection: ${collection.title}, Handle: ${collection.handle}, Count: ${productCount}`);
      });
      
      console.log(`Fetched product counts for ${Object.keys(productCountMap).length} collections`);
      console.log('Product count map:', productCountMap);
    }
    
    // Save the product counts to cache
    saveCollectionCountsToCache(productCountMap);
    
    return productCountMap;
  } catch (error) {
    console.error('Error fetching collection product counts:', error);
    // Fallback to fetching collections one by one with product counts
    const alternativeCounts = await fetchCollectionProductCountsAlternative();
    
    // Save the alternative product counts to cache as well
    saveCollectionCountsToCache(alternativeCounts);
    
    return alternativeCounts;
  }
};

// Alternative method to fetch collection product counts
async function fetchCollectionProductCountsAlternative() {
  try {
    console.log('Using alternative method to fetch collection product counts...');
    
    // First fetch all collections
    const collections = await fetchAllCollections();
    console.log(`Fetched ${collections.length} collections`);
    
    // Then fetch each collection's products to get the count
    const productCountMap: Record<string, number> = {};
    
    // For each collection, fetch its products and count them
    for (const collection of collections) {
      try {
        // Use the fetchProductsByCollectionHandle function to get products
        const result = await fetchProductsByCollectionHandle(collection.handle);
        const productCount = result.products ? result.products.length : 0;
        productCountMap[collection.handle] = productCount;
        console.log(`Collection: ${collection.title}, Handle: ${collection.handle}, Count: ${productCount}`);
      } catch (error) {
        console.error(`Error fetching products for collection ${collection.handle}:`, error);
        // Set a default count of 0 if there's an error
        productCountMap[collection.handle] = 0;
      }
    }
    
    console.log(`Fetched actual product counts for ${Object.keys(productCountMap).length} collections`);
    return productCountMap;
  } catch (error) {
    console.error('Error in alternative method for fetching collection product counts:', error);
    return {};
  }
}

/**
 * Fetches search suggestions using the backend proxy to Shopify's search API
 * @param query The search query
 * @param limit The maximum number of results to return (default: 6)
 * @returns Search suggestions including products, collections, and queries
 */
export async function fetchSearchSuggestions(query: string, limit: number = 6) {
  try {
    console.log(`Fetching search suggestions for query: ${query}, limit: ${limit}`);
    
    // Track search in Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'search', {
        search_term: query
      });
    }
    
    // Use the backend proxy endpoint that forwards requests to Shopify
    // This avoids CORS issues and gives the exact same results as Shopify's native search
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const response = await fetch(`${backendUrl}/api/shopify/search-suggestions?q=${encodeURIComponent(query)}`);
    
    if (!response.ok) {
      throw new Error(`Error fetching search suggestions: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Format the results to match our expected structure
    // The structure depends on Shopify's response format
    const suggestions = {
      products: (data.resources?.results?.products || []).map((product: any) => ({
        id: product.id,
        title: product.title,
        handle: product.handle,
        image: product.image || product.featured_image?.url || '',
        price: product.price || product.price_min
      })),
      collections: (data.resources?.results?.collections || []).map((collection: any) => ({
        id: collection.id,
        title: collection.title,
        handle: collection.handle
      })),
      // Extract just the text from query objects
      queries: (data.resources?.results?.queries || []).map((query: any) => query.text)
    };
    
    console.log(`Found ${suggestions.products.length} product suggestions, ${suggestions.collections.length} collection suggestions, and ${suggestions.queries.length} query suggestions`);
    
    return suggestions;
  } catch (error) {
    console.error('Error fetching search suggestions:', error);
    // Fall back to Storefront API if backend proxy fails
    console.log('Falling back to Storefront API...');
    return await fetchSearchSuggestionsViaStorefrontAPI(query, limit);
  }
}

/**
 * Main method to fetch search suggestions using Shopify's Storefront API
 * @param query The search query
 * @param limit The maximum number of results to return (default: 6)
 * @returns Search suggestions including products, collections, and queries
 */
async function fetchSearchSuggestionsViaStorefrontAPI(query: string, limit: number = 6) {
  try {
    console.log(`Using Storefront API for search suggestions. Query: ${query}, limit: ${limit}`);
    
    // Get the Shopify domain and access token from environment variables
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]*/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]*/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]*/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query for predictive search
    // We're using a more specific query to get better results that match Shopify's native search
    const graphqlQuery = `
      query predictiveSearch($query: String!, $types: [PredictiveSearchType!], $limit: Int!) {
        predictiveSearch(query: $query, types: $types, limit: $limit) {
          products {
            id
            title
            handle
            description
            productType
            vendor
            featuredImage {
              url
              altText
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                }
              }
            }
            tags
          }
          collections {
            id
            title
            handle
            description
          }
          queries {
            text
            styledText
          }
        }
      }
    `;

    const variables = {
      query,
      types: ["PRODUCT", "COLLECTION", "QUERY"],
      limit: limit
    };

    // Execute the GraphQL query with proper CORS headers
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query: graphqlQuery, variables }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return { products: [], collections: [], queries: [] };
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return { products: [], collections: [], queries: [] };
    }

    const predictiveSearchResults = result.data?.predictiveSearch || {};

    // Format the results to match our expected structure
    const suggestions = {
      products: predictiveSearchResults.products?.map((product: any) => ({
        id: product.id,
        title: product.title,
        handle: product.handle,
        description: product.description,
        image: product.featuredImage?.url || '',
        price: product.variants?.edges[0]?.node?.price?.amount,
        vendor: product.vendor,
        productType: product.productType,
        available: product.variants?.edges[0]?.node?.availableForSale
      })) || [],
      collections: predictiveSearchResults.collections?.map((collection: any) => ({
        id: collection.id,
        title: collection.title,
        handle: collection.handle,
        description: collection.description
      })) || [],
      queries: predictiveSearchResults.queries?.map((query: any) => query.text) || []
    };

    console.log(`Found ${suggestions.products.length} product suggestions, ${suggestions.collections.length} collection suggestions, and ${suggestions.queries.length} query suggestions via Storefront API`);

    return suggestions;
  } catch (error) {
    console.error('Error fetching search suggestions via Storefront API:', error);
    return { products: [], collections: [], queries: [] };
  }
}

// Function to fetch all pages from Shopify
export async function fetchAllPages(cursor?: string, limit: number = 20) {
  try {
    console.log(`Fetching all pages, cursor: ${cursor || 'initial'}, limit: ${limit}`);
    
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    // Create a GraphQL query to fetch pages with pagination
    const afterCursor = cursor ? `, after: "${cursor}"` : '';
    
    const query = `
      {
        pages(first: ${limit}${afterCursor}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              title
              handle
              bodySummary
              body
              createdAt
              updatedAt
              onlineStoreUrl
            }
          }
        }
      }
    `;

    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return { pages: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return { pages: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }

    // Extract pages from the response
    const pages = result.data?.pages?.edges?.map((edge: any) => ({
      id: edge.node.id,
      title: edge.node.title,
      handle: edge.node.handle,
      bodySummary: edge.node.bodySummary,
      body: edge.node.body,
      createdAt: edge.node.createdAt,
      updatedAt: edge.node.updatedAt,
      url: edge.node.onlineStoreUrl
    })) || [];

    // Extract pagination info
    const pageInfo = result.data?.pages?.pageInfo || { hasNextPage: false, endCursor: null };

    console.log(`Found ${pages.length} pages`);
    return { pages, pageInfo };
  } catch (error) {
    console.error('Error fetching pages:', error);
    return { pages: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

// Function to fetch a single page by handle
export async function fetchPageByHandle(handle: string) {
  try {
    console.log(`Fetching page with handle: ${handle}`);
    
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/['"]/g, '').split(';')[0].trim();
const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/['"]/g, '').split(';')[0].trim();
const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/['"]/g, '').split(';')[0].trim() || '2023-10';
    
    const query = `
      {
        pageByHandle(handle: "${handle}") {
          id
          title
          handle
          bodySummary
          body
          createdAt
          updatedAt
          onlineStoreUrl
        }
      }
    `;

    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return null;
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return null;
    }

    // Extract page from the response
    const page = result.data?.pageByHandle;
    
    if (!page) {
      console.log(`No page found with handle: ${handle}`);
      return null;
    }

    return {
      id: page.id,
      title: page.title,
      handle: page.handle,
      bodySummary: page.bodySummary,
      body: page.body,
      createdAt: page.createdAt,
      updatedAt: page.updatedAt,
      url: page.onlineStoreUrl
    };
  } catch (error) {
    console.error('Error fetching page by handle:', error);
    return null;
  }
}

// Function to fetch all blogs from Shopify
export async function fetchAllBlogs() {
  try {
    console.log('Fetching all blogs');
    
    // We need to fetch blogs using the Storefront API with GraphQL
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/["']/g, '').trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/["']/g, '').trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/["']/g, '').trim() || '2023-10';
    
    const query = `
      {
        blogs(first: 10) {
          edges {
            node {
              id
              handle
              title
            }
          }
        }
      }
    `;

    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return [];
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return [];
    }

    // Extract blogs from the response
    const blogs = result.data?.blogs?.edges?.map((edge: any) => ({
      id: edge.node.id,
      handle: edge.node.handle,
      title: edge.node.title,
      url: edge.node.url
    })) || [];

    console.log(`Found ${blogs.length} blogs`);
    return blogs;
  } catch (error) {
    console.error('Error fetching blogs:', error);
    return [];
  }
}

// Function to fetch a specific blog by its handle
export async function fetchBlogByHandle(handle: string) {
  try {
    console.log(`Fetching blog with handle: ${handle}`);
    
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/["']/g, '').trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/["']/g, '').trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/["']/g, '').trim() || '2023-10';
    
    const query = `
      {
        blog(handle: "${handle}") {
          id
          handle
          title
          url
        }
      }
    `;

    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return null;
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return null;
    }

    // Extract blog from the response
    const blog = result.data?.blog;
    
    if (!blog) {
      console.log(`No blog found with handle: ${handle}`);
      return null;
    }

    return {
      id: blog.id,
      handle: blog.handle,
      title: blog.title,
      url: blog.url
    };
  } catch (error) {
    console.error('Error fetching blog by handle:', error);
    return null;
  }
}

// Function to fetch articles from a specific blog with pagination
export async function fetchArticlesByBlogHandle(blogHandle: string, cursor?: string, limit: number = 20) {
  try {
    console.log(`Fetching articles for blog: ${blogHandle}, cursor: ${cursor || 'initial'}, limit: ${limit}`);
    
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/["']/g, '').trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/["']/g, '').trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/["']/g, '').trim() || '2023-10';
    
    // Create a GraphQL query to fetch articles with pagination
    const afterCursor = cursor ? `, after: "${cursor}"` : '';
    
    const query = `
      {
        blog(handle: "${blogHandle}") {
          articles(first: ${limit}${afterCursor}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                handle
                title
                excerpt
                content
                contentHtml
                publishedAt
                image {
                  url
                  altText
                }
                author {
                  name
                }
                tags
                blog {
                  handle
                }
              }
            }
          }
        }
      }
    `;

    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return { articles: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return { articles: [], pageInfo: { hasNextPage: false, endCursor: null } };
    }

    // Extract articles and pagination info from the response
    const articles = result.data?.blog?.articles?.edges?.map((edge: any) => {
      const article = edge.node;
      return {
        id: article.id,
        handle: article.handle,
        title: article.title,
        excerpt: article.excerpt,
        content: article.content,
        contentHtml: article.contentHtml,
        publishedAt: article.publishedAt,
        image: article.image ? {
          url: article.image.url,
          altText: article.image.altText
        } : null,
        author: article.author ? {
          name: article.author.name
        } : null,
        tags: article.tags || [],
        blogHandle: article.blog?.handle || blogHandle
      };
    }) || [];

    // Extract pagination info
    const pageInfo = result.data?.blog?.articles?.pageInfo || { hasNextPage: false, endCursor: null };

    console.log(`Found ${articles.length} articles for blog: ${blogHandle}`);
    return { articles, pageInfo };
  } catch (error) {
    console.error(`Error fetching articles for blog ${blogHandle}:`, error);
    return { articles: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

// Function to fetch a specific article by its handle from a blog
export async function fetchArticleByHandle(blogHandle: string, articleHandle: string) {
  try {
    console.log(`Fetching article with handle: ${articleHandle} from blog: ${blogHandle}`);
    
    const domain = (process.env.REACT_APP_SHOPIFY_SHOP || '').replace(/["']/g, '').trim();
    const storefrontAccessToken = (process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN || '').replace(/["']/g, '').trim();
    const apiVersion = (process.env.REACT_APP_SHOPIFY_API_VERSION || '').replace(/["']/g, '').trim() || '2023-10';
    
    const query = `
      {
        blog(handle: "${blogHandle}") {
          articleByHandle(handle: "${articleHandle}") {
            id
            handle
            title
            excerpt
            content
            contentHtml
            publishedAt
            image {
              url
              altText
            }
            author {
              name
            }
            tags
          }
        }
      }
    `;

    // Execute the GraphQL query
    const url = `https://${domain}/api/${apiVersion}/graphql.json`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      console.error(`Storefront API responded with status ${response.status}`);
      return null;
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return null;
    }

    // Extract article from the response
    const article = result.data?.blog?.articleByHandle;
    
    if (!article) {
      console.log(`No article found with handle: ${articleHandle} in blog: ${blogHandle}`);
      return null;
    }

    return {
      id: article.id,
      handle: article.handle,
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      contentHtml: article.contentHtml,
      publishedAt: article.publishedAt,
      image: article.image ? {
        url: article.image.url,
        altText: article.image.altText
      } : null,
      author: article.author ? {
        name: article.author.name
      } : null,
      tags: article.tags || [],
      blogHandle: blogHandle
    };
  } catch (error) {
    console.error(`Error fetching article with handle ${articleHandle} from blog ${blogHandle}:`, error);
    return null;
  }
}

export default {
  fetchAllProducts,
  fetchProductById,
  fetchProductsByCollection,
  fetchProductsByCollectionHandle,
  searchProducts,
  fetchSearchSuggestions,
  fetchAllCollections,
  fetchShopifyMenu,
  fetchCollectionProductCounts,
  fetchAllPages,
  fetchPageByHandle,
  fetchAllBlogs,
  fetchBlogByHandle,
  fetchArticlesByBlogHandle,
  fetchArticleByHandle,
  shopifyClient
};
